import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Package, FileText, Download, Truck, Calendar, LayoutDashboard, Archive as ArchiveIcon, ChevronDown, ChevronRight, Trash2, Pencil, Check, X } from 'lucide-react';
import { TrackingDialog } from '../components/TrackingDialog';
import { InvoicesPanel } from '../components/InvoicesPanel';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

export function TransportSuivi() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'partenaire' && user?.partner_type === 'manager';

  const [packages, setPackages] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [archiveOrders, setArchiveOrders] = useState([]);
  const [uninvoicedItems, setUninvoicedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('orders_active');
  const [trackingDetailOpen, setTrackingDetailOpen] = useState(false);
  const [trackingDetailNumber, setTrackingDetailNumber] = useState('');
  const [expandedMonths, setExpandedMonths] = useState({});
  const [expandedColis, setExpandedColis] = useState({});
  const [editingWP, setEditingWP] = useState(null); // { id, type:'order'|'package', weight, price }
  const [editingTracking, setEditingTracking] = useState(null); // { id, type:'order'|'package', number, link }

  const fetchData = useCallback(async () => {
    try {
      const [pkgRes, invRes, ordersRes, archivesRes, uninvRes] = await Promise.all([
        axios.get(`${API}/api/transport/packages`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/transport/invoices`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/transport-orders`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/transport-orders/archives`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/transport/uninvoiced-items`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setPackages(pkgRes.data || []);
      setInvoices(invRes.data || []);
      setOrders(ordersRes.data || []);
      setArchiveOrders(archivesRes.data || []);
      setUninvoicedItems(uninvRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const unpaidCount = invoices.filter(i => !i.paid).length;

  const handleMarkPaid = async (invoiceId) => {
    try {
      await axios.put(`${API}/api/transport/invoices/${invoiceId}/pay`, {}, { withCredentials: true });
      toast.success('Facture marquée comme payée');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleMarkUnpaid = async (invoiceId) => {
    try {
      await axios.put(`${API}/api/transport/invoices/${invoiceId}/unpay`, {}, { withCredentials: true });
      toast.success('Facture marquée comme non payée');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDownloadPdf = async (invoiceId, invoiceNum) => {
    try {
      const res = await axios.get(`${API}/api/transport/invoices/${invoiceId}/pdf`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Facture_Transport_${invoiceNum}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) { toast.error('Erreur téléchargement PDF'); }
  };

  const handleDeleteInvoice = async (invoiceId, invoiceNum) => {
    if (!window.confirm(`Supprimer la facture N°${invoiceNum} ? Les colis associés redeviendront non facturés.`)) return;
    try {
      await axios.delete(`${API}/api/transport/invoices/${invoiceId}`, { withCredentials: true });
      toast.success(`Facture N°${invoiceNum} supprimée`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur suppression'); }
  };

  const handleDeletePackage = async (packageId) => {
    if (!window.confirm('Supprimer ce colis ? Les commandes associées redeviendront non validées.')) return;
    try {
      await axios.delete(`${API}/api/transport/packages/${packageId}`, { withCredentials: true });
      toast.success('Colis supprimé');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur suppression'); }
  };

  const handleSaveWeightPrice = async () => {
    if (!editingWP) return;
    const { id, type, weight, price } = editingWP;
    try {
      if (type === 'package') {
        await axios.put(`${API}/api/transport/packages/${id}/validate`, { weight: parseFloat(weight) || 0, price: parseFloat(price) || 0 }, { withCredentials: true });
      } else {
        await axios.put(`${API}/api/transport-orders/${id}/admin-update-package`, { package_weight: parseFloat(weight) || 0, package_price_vnd: parseFloat(price) || 0 }, { withCredentials: true });
      }
      toast.success('Poids/Prix mis à jour');
      setEditingWP(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleSaveTracking = async () => {
    if (!editingTracking) return;
    const { id, type, number, link } = editingTracking;
    try {
      if (type === 'package') {
        await axios.put(`${API}/api/transport/packages/${id}/tracking`, { tracking_number: number || '', tracking_link: link || '' }, { withCredentials: true });
      } else {
        await axios.put(`${API}/api/transport-orders/${id}/tracking`, { tracking_number: number || '', tracking_link: link || '' }, { withCredentials: true });
      }
      toast.success('Tracking mis à jour');
      setEditingTracking(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const TrackingCell = ({ id, type, trackingNumber, trackingLink, isChild }) => {
    if (isChild) return <span className="text-xs text-slate-400">—</span>;
    const hasTrack = !!(trackingNumber?.trim() || trackingLink?.trim());
    const isEditing = editingTracking?.id === id;
    if (isEditing) {
      return (
        <div className="flex flex-col gap-1" onClick={e => e.stopPropagation()}>
          <Input type="text" value={editingTracking.number} onChange={e => setEditingTracking(prev => ({ ...prev, number: e.target.value }))} className="h-7 w-32 text-xs" placeholder="N° tracking" />
          <Input type="text" value={editingTracking.link} onChange={e => setEditingTracking(prev => ({ ...prev, link: e.target.value }))} className="h-7 w-32 text-xs" placeholder="Lien (optionnel)" />
          <div className="flex gap-1">
            <Button size="sm" className="h-6 text-[10px] bg-emerald-600 hover:bg-emerald-700 gap-0.5 px-2" onClick={handleSaveTracking}><Check size={10} /></Button>
            <Button size="sm" variant="outline" className="h-6 text-[10px] gap-0.5 px-2" onClick={() => setEditingTracking(null)}><X size={10} /></Button>
          </div>
        </div>
      );
    }
    if (hasTrack) {
      return (
        <div className="flex items-center gap-1">
          <span className="text-xs font-medium text-slate-700">{trackingNumber}</span>
          <Button size="sm" className="h-5 text-[10px] gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => { setTrackingDetailNumber(trackingNumber); setTrackingDetailOpen(true); }}>
            <Package size={10} /> Suivi
          </Button>
          {isAdmin && (
            <button className="text-[10px] text-blue-600 hover:underline ml-1" onClick={() => setEditingTracking({ id, type, number: trackingNumber || '', link: trackingLink || '' })}>
              <Pencil size={9} />
            </button>
          )}
        </div>
      );
    }
    return (
      <div className="text-xs" onClick={e => e.stopPropagation()}>
        <span className="text-slate-400">—</span>
        {isAdmin && (
          <button className="mt-1 flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 hover:underline"
            onClick={() => setEditingTracking({ id, type, number: '', link: '' })}>
            <Pencil size={10} /> Ajouter
          </button>
        )}
      </div>
    );
  };

  const WeightPriceCell = ({ id, type, weight, price, isChild }) => {
    if (isChild) return <span className="text-xs text-slate-400">—</span>;
    const isEditing = editingWP?.id === id;
    if (isEditing) {
      return (
        <div className="flex flex-col gap-1" onClick={e => e.stopPropagation()}>
          <div className="flex items-center gap-1">
            <Input type="number" step="0.1" min="0" value={editingWP.weight} onChange={e => setEditingWP(prev => ({ ...prev, weight: e.target.value }))} className="h-7 w-20 text-xs" placeholder="kg" />
            <span className="text-[10px] text-slate-400">kg</span>
          </div>
          <div className="flex items-center gap-1">
            <Input type="number" step="1000" min="0" value={editingWP.price} onChange={e => setEditingWP(prev => ({ ...prev, price: e.target.value }))} className="h-7 w-24 text-xs" placeholder="VND" />
            <span className="text-[10px] text-slate-400">đ</span>
          </div>
          <div className="flex gap-1">
            <Button size="sm" className="h-6 text-[10px] bg-emerald-600 hover:bg-emerald-700 gap-0.5 px-2" onClick={handleSaveWeightPrice}><Check size={10} /></Button>
            <Button size="sm" variant="outline" className="h-6 text-[10px] gap-0.5 px-2" onClick={() => setEditingWP(null)}><X size={10} /></Button>
          </div>
        </div>
      );
    }
    const hasValues = (weight > 0 || price > 0);
    return (
      <div className="text-xs" onClick={e => e.stopPropagation()}>
        {hasValues ? (
          <>
            {weight > 0 && <div className="font-bold text-emerald-700">{weight} kg</div>}
            {price > 0 && <div className="font-bold">{(price || 0).toLocaleString()} VND</div>}
          </>
        ) : (
          <span className="text-slate-400">—</span>
        )}
        {isAdmin && (
          <button className="mt-1 flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 hover:underline"
            onClick={() => setEditingWP({ id, type, weight: weight || '', price: price || '' })}>
            <Pencil size={10} /> {hasValues ? 'Modifier' : 'Ajouter'}
          </button>
        )}
      </div>
    );
  };

  const toggleMonth = (label) => setExpandedMonths(prev => ({ ...prev, [label]: !prev[label] }));

  const groupByMonth = (list) => {
    const groups = {};
    for (const o of list) {
      const label = o.month_label || 'Inconnu';
      if (!groups[label]) groups[label] = [];
      groups[label].push(o);
    }
    return Object.entries(groups).map(([label, items]) => ({ label, orders: items }));
  };

  const DeliveryCell = ({ order }) => {
    if (order.tracking_delivered_date) {
      return (
        <span className="text-xs font-bold text-emerald-700">
          Livré {new Date(order.tracking_delivered_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
        </span>
      );
    }
    if (order.tracking_first_event_date) {
      const est = new Date(new Date(order.tracking_first_event_date).getTime() + 15 * 24 * 60 * 60 * 1000);
      const overdue = est.toISOString().slice(0, 10) < new Date().toISOString().slice(0, 10);
      return (
        <span className={`text-xs font-bold ${overdue ? 'text-red-600 animate-pulse' : 'text-emerald-600'}`}>
          Est. {est.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
        </span>
      );
    }
    return <span className="text-xs text-slate-400">—</span>;
  };

  const sidebarItems = [
    { id: 'orders_active', label: 'Commandes en cours', icon: LayoutDashboard, badge: orders.length },
    { id: 'orders_archives', label: 'Archives', icon: ArchiveIcon, badge: archiveOrders.length },
    { id: 'invoices', label: 'Factures Transport', icon: FileText, badge: unpaidCount > 0 ? unpaidCount : null, badgeColor: 'red' },
    { id: 'recap', label: 'Récap Mensuel', icon: Calendar, badge: null },
  ];

  return (
    <div className="flex gap-6" data-testid="transport-suivi-card">
      {/* Left sidebar */}
      <aside className="w-52 flex-shrink-0">
        <nav className="space-y-1">
          {sidebarItems.map(item => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                data-testid={`transport-suivi-tab-${item.id}`}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-slate-900 text-white'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <Icon size={16} />
                <span className="truncate">{item.label}</span>
                {item.badge != null && item.badge > 0 && (
                  <span className={`ml-auto text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1 ${
                    item.badgeColor === 'red'
                      ? 'bg-red-500 text-white'
                      : isActive ? 'bg-white text-slate-900' : 'bg-slate-200 text-slate-700'
                  }`}>
                    {item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Main content */}
      <div className="flex-1 min-w-0">
        {loading && <p className="text-sm text-slate-500 text-center py-8">Chargement...</p>}

        {/* COMMANDES EN COURS */}
        {!loading && activeTab === 'orders_active' && (
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Truck size={20} className="text-slate-600" />
                <CardTitle className="text-lg font-medium">
                  Commandes en cours de livraison ({orders.length})
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {orders.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">Aucune commande en cours</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="font-medium">N° commande</TableHead>
                        <TableHead className="font-medium">Client</TableHead>
                        <TableHead className="font-medium">Pays</TableHead>
                        <TableHead className="font-medium">Statut</TableHead>
                        <TableHead className="font-medium">Tracking</TableHead>
                        <TableHead className="font-medium">Poids / Prix</TableHead>
                        <TableHead className="font-medium">Livraison</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        const ungrouped = [];
                        const colisGroups = {};
                        orders.forEach(order => {
                          const pid = order.transport_package_id;
                          if (pid) {
                            if (!colisGroups[pid]) colisGroups[pid] = { orders: [], name: order.transport_package_name, weight: order.transport_package_weight, price: order.transport_package_price };
                            colisGroups[pid].orders.push(order);
                          } else {
                            ungrouped.push(order);
                          }
                        });
                        const rows = [];
                        ungrouped.forEach(o => rows.push({ type: 'order', order: o }));
                        let colisIdx = 0;
                        Object.entries(colisGroups).forEach(([pkgId, group]) => {
                          colisIdx++;
                          const colisTrack = group.orders.find(o => o.tracking_number?.trim() || o.tracking_link?.trim());
                          rows.push({ type: 'colis', pkgId, name: group.name || `Colis #${colisIdx}`, count: group.orders.length, weight: group.weight, price: group.price, trackingNumber: colisTrack?.tracking_number || '', trackingLink: colisTrack?.tracking_link || '' });
                          group.orders.forEach(o => rows.push({ type: 'child', order: o, pkgId }));
                        });
                        return rows.map((row) => {
                          if (row.type === 'colis') {
                            const isExp = expandedColis[row.pkgId];
                            return (
                              <TableRow key={`colis-${row.pkgId}`} className="bg-blue-50 border-l-4 border-l-blue-500 cursor-pointer hover:bg-blue-100" onClick={() => setExpandedColis(prev => ({ ...prev, [row.pkgId]: !prev[row.pkgId] }))}>
                                <TableCell colSpan={4} className="py-2">
                                  <div className="flex items-center gap-3">
                                    {isExp ? <ChevronDown size={16} className="text-blue-600" /> : <ChevronRight size={16} className="text-blue-600" />}
                                    <Package size={16} className="text-blue-600" />
                                    <span className="font-bold text-blue-800 text-sm">{row.name}</span>
                                    <span className="text-xs text-blue-600">({row.count} commandes)</span>
                                  </div>
                                </TableCell>
                                <TableCell><TrackingCell id={row.pkgId} type="package" trackingNumber={row.trackingNumber} trackingLink={row.trackingLink} /></TableCell>
                                <TableCell><WeightPriceCell id={row.pkgId} type="package" weight={row.weight} price={row.price} /></TableCell>
                                <TableCell></TableCell>
                              </TableRow>
                            );
                          }
                          if (row.type === 'child' && !expandedColis[row.pkgId]) return null;
                          const order = row.order;
                          return (
                            <TableRow key={order._id} data-testid="transport-active-order-row" className={`hover:bg-slate-50 ${row.type === 'child' ? 'bg-blue-50/20' : ''}`}>
                              <TableCell className="font-medium text-sm">{order.order_number}</TableCell>
                              <TableCell className="text-sm">{order.client_name}</TableCell>
                              <TableCell className="text-sm">{order.client_country}</TableCell>
                              <TableCell>
                                <Badge className="bg-blue-100 text-blue-800 border border-blue-200 text-xs uppercase">{order.status}</Badge>
                              </TableCell>
                              <TableCell>
                                <TrackingCell id={order._id} type="order" trackingNumber={order.tracking_number} trackingLink={order.tracking_link} isChild={row.type === 'child'} />
                              </TableCell>
                              <TableCell>
                                <WeightPriceCell id={order._id} type="order" weight={order.package_weight} price={order.package_price_vnd} isChild={row.type === 'child'} />
                              </TableCell>
                              <TableCell><DeliveryCell order={order} /></TableCell>
                            </TableRow>
                          );
                        });
                      })()}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ARCHIVES */}
        {!loading && activeTab === 'orders_archives' && (
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <div className="flex items-center gap-2">
                <ArchiveIcon size={20} className="text-slate-600" />
                <CardTitle className="text-lg font-medium">
                  Archives Transport ({archiveOrders.length})
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {archiveOrders.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">Aucune archive</p>
              ) : (
                <div className="space-y-3">
                  {groupByMonth(archiveOrders).map(group => (
                    <div key={group.label} className="border border-slate-200 rounded-lg overflow-hidden">
                      <button
                        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                        onClick={() => toggleMonth(group.label)}
                      >
                        <div className="flex items-center gap-3">
                          {expandedMonths[group.label] ? <ChevronDown size={18} className="text-slate-500" /> : <ChevronRight size={18} className="text-slate-500" />}
                          <span className="font-semibold text-slate-900">{group.label}</span>
                        </div>
                        <Badge className="bg-slate-200 text-slate-700 border-0">{group.orders.length}</Badge>
                      </button>
                      {expandedMonths[group.label] && (
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-white">
                                <TableHead className="font-medium">N° commande</TableHead>
                                <TableHead className="font-medium">Client</TableHead>
                                <TableHead className="font-medium">Pays</TableHead>
                                <TableHead className="font-medium">Date</TableHead>
                                <TableHead className="font-medium">Statut</TableHead>
                                <TableHead className="font-medium">Poids / Prix</TableHead>
                                <TableHead className="font-medium">Livraison</TableHead>
                                <TableHead className="font-medium">Suivi</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {(() => {
                                const ungrouped = [];
                                const colisGroups = {};
                                group.orders.forEach(order => {
                                  const pid = order.transport_package_id;
                                  if (pid) {
                                    if (!colisGroups[pid]) colisGroups[pid] = { orders: [], name: order.transport_package_name, weight: order.transport_package_weight, price: order.transport_package_price };
                                    colisGroups[pid].orders.push(order);
                                  } else {
                                    ungrouped.push(order);
                                  }
                                });
                                const rows = [];
                                ungrouped.forEach(o => rows.push({ type: 'order', order: o }));
                                let ci = 0;
                                Object.entries(colisGroups).forEach(([pkgId, g]) => {
                                  ci++;
                                  rows.push({ type: 'colis', pkgId, name: g.name || `Colis #${ci}`, count: g.orders.length, weight: g.weight, price: g.price });
                                  g.orders.forEach(o => rows.push({ type: 'child', order: o, pkgId }));
                                });
                                return rows.map((row) => {
                                  if (row.type === 'colis') {
                                    const isExp = expandedColis[row.pkgId];
                                    return (
                                      <TableRow key={`colis-${row.pkgId}`} className="bg-blue-50 border-l-4 border-l-blue-500 cursor-pointer hover:bg-blue-100" onClick={() => setExpandedColis(prev => ({ ...prev, [row.pkgId]: !prev[row.pkgId] }))}>
                                        <TableCell colSpan={5} className="py-2">
                                          <div className="flex items-center gap-3">
                                            {isExp ? <ChevronDown size={16} className="text-blue-600" /> : <ChevronRight size={16} className="text-blue-600" />}
                                            <Package size={16} className="text-blue-600" />
                                            <span className="font-bold text-blue-800 text-sm">{row.name}</span>
                                            <span className="text-xs text-blue-600">({row.count} commandes)</span>
                                          </div>
                                        </TableCell>
                                        <TableCell><WeightPriceCell id={row.pkgId} type="package" weight={row.weight} price={row.price} /></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                      </TableRow>
                                    );
                                  }
                                  if (row.type === 'child' && !expandedColis[row.pkgId]) return null;
                                  const order = row.order;
                                  return (
                                <TableRow key={order._id} data-testid="transport-archive-order-row" className={`hover:bg-slate-50 ${row.type === 'child' ? 'bg-blue-50/20' : ''}`}>
                                  <TableCell className="font-medium text-sm">{order.order_number}</TableCell>
                                  <TableCell className="text-sm">{order.client_name}</TableCell>
                                  <TableCell className="text-sm">{order.client_country}</TableCell>
                                  <TableCell className="text-xs">{order.order_date ? new Date(order.order_date).toLocaleDateString('fr-FR') : '—'}</TableCell>
                                  <TableCell>
                                    <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-xs uppercase">{order.status}</Badge>
                                  </TableCell>
                                  <TableCell>
                                    <WeightPriceCell id={order._id} type="order" weight={order.package_weight} price={order.package_price_vnd} isChild={row.type === 'child'} />
                                  </TableCell>
                                  <TableCell><DeliveryCell order={order} /></TableCell>
                                  <TableCell>
                                    {order.tracking_number && (
                                      <Button size="sm" className="h-6 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                        onClick={() => { setTrackingDetailNumber(order.tracking_number); setTrackingDetailOpen(true); }}>
                                        <Package size={12} /> Suivi
                                      </Button>
                                    )}
                                  </TableCell>
                                </TableRow>
                                  );
                                });
                              })()}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* FACTURES (Admin view with countries, detail, payment) */}
        {!loading && activeTab === 'invoices' && (() => {
          const unpaidInvoices = invoices.filter(i => !i.paid);
          const paidInvoices = invoices.filter(i => i.paid);
          return (
            <div className="space-y-6">
              {/* Uninvoiced items */}
              <InvoicesPanel
                uninvoicedItems={uninvoicedItems}
                invoices={[]} // We handle invoices ourselves below
                onCreateInvoice={async (items) => {
                  if (!items.length) return;
                  try {
                    const res = await axios.post(`${API}/api/transport/invoices`, { items }, { withCredentials: true });
                    toast.success(`Facture N°${res.data.invoice_number} créée`);
                    fetchData();
                  } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
                }}
                onDownloadPdf={handleDownloadPdf}
                onMarkPaid={handleMarkPaid}
                onMarkUnpaid={handleMarkUnpaid}
                onDeleteInvoice={handleDeleteInvoice}
                onDeletePackage={handleDeletePackage}
                onIgnoreItem={async (itemId, itemType) => {
                  try {
                    await axios.put(`${API}/api/transport/ignore-item`, { item_id: itemId, item_type: itemType }, { withCredentials: true });
                    fetchData();
                  } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
                }}
                canDelete={isAdmin}
                canPay={isAdmin || isManager}
                canIgnore={isAdmin || isManager}
                t={t}
              />

              {/* FACTURES EN ATTENTE */}
              {unpaidInvoices.length > 0 && (
                <Card className="border-amber-200 shadow-sm" data-testid="admin-unpaid-invoices">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <FileText size={18} className="text-amber-600" />
                      <CardTitle className="text-base font-medium">Factures en attente</CardTitle>
                      <Badge className="bg-amber-100 text-amber-800 border-0 text-xs">{unpaidInvoices.length}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-amber-50/50">
                          <TableHead className="font-medium">N° Facture</TableHead>
                          <TableHead className="font-medium">Date</TableHead>
                          <TableHead className="font-medium">Pays</TableHead>
                          <TableHead className="font-medium">Détail</TableHead>
                          <TableHead className="font-medium">Poids</TableHead>
                          <TableHead className="font-medium">Total</TableHead>
                          <TableHead className="font-medium">PDF</TableHead>
                          {(isAdmin || isManager) && <TableHead className="font-medium">Paiement</TableHead>}
                          {isAdmin && <TableHead className="w-10"></TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {unpaidInvoices.map(inv => (
                          <React.Fragment key={inv._id}>
                            <TableRow className="bg-amber-50/30 hover:bg-amber-50">
                              <TableCell className="font-bold text-sm">{inv.invoice_number}</TableCell>
                              <TableCell className="text-xs">{inv.date?.slice(0, 10)}</TableCell>
                              <TableCell className="text-xs">{(inv.countries || []).join(', ') || '—'}</TableCell>
                              <TableCell>
                                <Button size="sm" variant="outline" className="h-6 text-xs gap-1"
                                  onClick={() => setExpandedColis(prev => ({ ...prev, [`inv-${inv._id}`]: !prev[`inv-${inv._id}`] }))}>
                                  {expandedColis[`inv-${inv._id}`] ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                  {inv.lines?.length || 0} colis
                                </Button>
                              </TableCell>
                              <TableCell className="text-xs">{inv.total_weight_kg?.toFixed(1)} kg</TableCell>
                              <TableCell className="text-xs font-semibold">{inv.total_vnd?.toLocaleString()}đ</TableCell>
                              <TableCell>
                                <Button size="sm" variant="outline" className="h-6 text-xs gap-1" onClick={() => handleDownloadPdf(inv._id, inv.invoice_number)}>
                                  <Download size={12} /> PDF
                                </Button>
                              </TableCell>
                              {(isAdmin || isManager) && (
                                <TableCell>
                                  <Checkbox checked={false} onCheckedChange={() => handleMarkPaid(inv._id)} />
                                </TableCell>
                              )}
                              {isAdmin && (
                                <TableCell>
                                  <Button size="sm" variant="ghost" className="h-6 text-xs text-red-600 hover:text-red-800 hover:bg-red-50"
                                    onClick={() => handleDeleteInvoice(inv._id, inv.invoice_number)}>
                                    <Trash2 size={12} />
                                  </Button>
                                </TableCell>
                              )}
                            </TableRow>
                            {expandedColis[`inv-${inv._id}`] && (
                              <TableRow>
                                <TableCell colSpan={isAdmin ? 9 : (isAdmin || isManager) ? 8 : 7} className="bg-slate-50 p-0">
                                  <div className="px-6 py-3">
                                    <p className="text-xs font-semibold text-slate-500 mb-2">Détail des colis :</p>
                                    {(inv.lines || []).map((line, i) => (
                                      <div key={i} className="flex items-center gap-4 text-xs py-1.5 border-b border-slate-100 last:border-0">
                                        <Package size={12} className="text-blue-500" />
                                        <span className="font-medium text-slate-700">{line.order_numbers?.join(', ')}</span>
                                        <span className="text-slate-500">{line.weight_kg} kg</span>
                                        <span className="font-semibold">{line.total_vnd?.toLocaleString()}đ</span>
                                      </div>
                                    ))}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* FACTURES PAYÉES */}
              <Card className="border-emerald-200 shadow-sm" data-testid="admin-paid-invoices">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <FileText size={18} className="text-emerald-600" />
                    <CardTitle className="text-base font-medium">Factures payées</CardTitle>
                    {paidInvoices.length > 0 && <Badge className="bg-emerald-100 text-emerald-800 border-0 text-xs">{paidInvoices.length}</Badge>}
                  </div>
                </CardHeader>
                <CardContent>
                  {paidInvoices.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-3">Aucune facture payée</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-emerald-50/50">
                          <TableHead className="font-medium">N° Facture</TableHead>
                          <TableHead className="font-medium">Date facture</TableHead>
                          <TableHead className="font-medium">Pays</TableHead>
                          <TableHead className="font-medium">Détail</TableHead>
                          <TableHead className="font-medium">Poids</TableHead>
                          <TableHead className="font-medium">Total</TableHead>
                          <TableHead className="font-medium">Date paiement</TableHead>
                          <TableHead className="font-medium">PDF</TableHead>
                          {(isAdmin || isManager) && <TableHead className="font-medium">Annuler</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paidInvoices.map(inv => (
                          <React.Fragment key={inv._id}>
                            <TableRow className="hover:bg-emerald-50/30">
                              <TableCell className="font-bold text-sm">{inv.invoice_number}</TableCell>
                              <TableCell className="text-xs">{inv.date?.slice(0, 10)}</TableCell>
                              <TableCell className="text-xs">{(inv.countries || []).join(', ') || '—'}</TableCell>
                              <TableCell>
                                <Button size="sm" variant="outline" className="h-6 text-xs gap-1"
                                  onClick={() => setExpandedColis(prev => ({ ...prev, [`inv-${inv._id}`]: !prev[`inv-${inv._id}`] }))}>
                                  {expandedColis[`inv-${inv._id}`] ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                  {inv.lines?.length || 0} colis
                                </Button>
                              </TableCell>
                              <TableCell className="text-xs">{inv.total_weight_kg?.toFixed(1)} kg</TableCell>
                              <TableCell className="text-xs font-semibold">{inv.total_vnd?.toLocaleString()}đ</TableCell>
                              <TableCell className="text-xs font-medium text-emerald-700">
                                {inv.paid_at ? new Date(inv.paid_at).toLocaleDateString('fr-FR') : '—'}
                              </TableCell>
                              <TableCell>
                                <Button size="sm" variant="outline" className="h-6 text-xs gap-1" onClick={() => handleDownloadPdf(inv._id, inv.invoice_number)}>
                                  <Download size={12} /> PDF
                                </Button>
                              </TableCell>
                              {(isAdmin || isManager) && (
                                <TableCell>
                                  <Checkbox checked={true} onCheckedChange={() => handleMarkUnpaid(inv._id)} />
                                </TableCell>
                              )}
                            </TableRow>
                            {expandedColis[`inv-${inv._id}`] && (
                              <TableRow>
                                <TableCell colSpan={(isAdmin || isManager) ? 9 : 8} className="bg-slate-50 p-0">
                                  <div className="px-6 py-3">
                                    <p className="text-xs font-semibold text-slate-500 mb-2">Détail des colis :</p>
                                    {(inv.lines || []).map((line, i) => (
                                      <div key={i} className="flex items-center gap-4 text-xs py-1.5 border-b border-slate-100 last:border-0">
                                        <Package size={12} className="text-slate-400" />
                                        <span className="font-medium text-slate-700">{line.order_numbers?.join(', ')}</span>
                                        <span className="text-slate-500">{line.weight_kg} kg</span>
                                        <span className="font-semibold">{line.total_vnd?.toLocaleString()}đ</span>
                                      </div>
                                    ))}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          );
        })()}

        {/* RECAP MENSUEL */}
        {!loading && activeTab === 'recap' && (() => {
          const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
          const monthlyData = {};
          // Helper to add an item to monthly data
          const addItem = (dateStr, weight, price) => {
            const d = (dateStr || '').slice(0, 7);
            if (!d) return;
            if (weight <= 0 && price <= 0) return;
            if (!monthlyData[d]) monthlyData[d] = { count: 0, totalWeight: 0, totalPrice: 0 };
            monthlyData[d].count += 1;
            monthlyData[d].totalWeight += weight;
            monthlyData[d].totalPrice += price;
          };
          // 1) Packages (colis)
          packages.forEach(pkg => {
            const dateField = pkg.transport_validated_at || pkg.created_at || '';
            addItem(dateField, pkg.weight_kg || 0, pkg.price_vnd || 0);
          });
          // 2) Individual orders (not in a colis) from both active + archives
          const allOrders = [...orders, ...archiveOrders];
          const seenIds = new Set();
          allOrders.forEach(o => {
            if (seenIds.has(o._id)) return;
            seenIds.add(o._id);
            if (o.transport_package_id) return; // skip orders that are part of a colis
            const w = o.package_weight || 0;
            const p = o.package_price_vnd || 0;
            if (w <= 0 && p <= 0) return;
            const dateField = o.transport_validated_at || o.order_date || '';
            addItem(dateField, w, p);
          });
          const sortedMonths = Object.keys(monthlyData).sort().reverse();
          const totalItems = sortedMonths.reduce((s, k) => s + monthlyData[k].count, 0);
          const totalWeight = sortedMonths.reduce((s, k) => s + monthlyData[k].totalWeight, 0);
          const totalPrice = sortedMonths.reduce((s, k) => s + monthlyData[k].totalPrice, 0);
          const totalAvg = totalWeight > 0 ? totalPrice / totalWeight : 0;
          return (
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Calendar size={20} className="text-slate-600" />
                  <CardTitle className="text-lg font-medium">Récapitulatif Mensuel</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {sortedMonths.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">Aucune donnée</p>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="font-medium">Mois</TableHead>
                          <TableHead className="font-medium text-center">Nb colis / commandes</TableHead>
                          <TableHead className="font-medium text-center">Poids total</TableHead>
                          <TableHead className="font-medium text-right">Prix total</TableHead>
                          <TableHead className="font-medium text-right">Prix moyen / kg</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedMonths.map(key => {
                          const [yr, mo] = key.split('-');
                          const data = monthlyData[key];
                          const label = `${MONTHS_FR[parseInt(mo) - 1]} ${yr}`;
                          const avgPerKg = data.totalWeight > 0 ? data.totalPrice / data.totalWeight : 0;
                          return (
                            <TableRow key={key} className="hover:bg-slate-50">
                              <TableCell className="font-semibold text-sm text-slate-800">{label}</TableCell>
                              <TableCell className="text-center">
                                <Badge className="bg-emerald-100 text-emerald-800 border-0 text-xs">{data.count}</Badge>
                              </TableCell>
                              <TableCell className="text-center text-sm">{data.totalWeight.toFixed(2)} kg</TableCell>
                              <TableCell className="text-right text-sm font-bold text-slate-900">{data.totalPrice.toLocaleString()}đ</TableCell>
                              <TableCell className="text-right text-sm font-semibold text-blue-700">{Math.round(avgPerKg).toLocaleString()}đ/kg</TableCell>
                            </TableRow>
                          );
                        })}
                        {/* Total row */}
                        <TableRow className="bg-slate-100 border-t-2 border-slate-300">
                          <TableCell className="font-bold text-sm text-slate-900">TOTAL</TableCell>
                          <TableCell className="text-center">
                            <Badge className="bg-slate-800 text-white border-0 text-xs">{totalItems}</Badge>
                          </TableCell>
                          <TableCell className="text-center text-sm font-bold">{totalWeight.toFixed(2)} kg</TableCell>
                          <TableCell className="text-right text-sm font-bold text-slate-900">{totalPrice.toLocaleString()}đ</TableCell>
                          <TableCell className="text-right text-sm font-bold text-blue-700">{Math.round(totalAvg).toLocaleString()}đ/kg</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })()}

        <TrackingDialog open={trackingDetailOpen} onOpenChange={setTrackingDetailOpen} trackingNumber={trackingDetailNumber} />
      </div>
    </div>
  );
}
